import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useSkeleton from "../../../../hooks/useSkeleton";

import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Chips } from "primereact/chips";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import { Image } from "primereact/image";

import ImageUpload from "../../../../components/fileUpload/fileUpload";

import "react-toastify/dist/ReactToastify.css";

import { atom, useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";

const ClientsForm = ({ settings }) => {
  const [isLoading, setIsLoading] = useAtom(loadingState);

  const s = useSkeleton("abm");
  const { t } = useTranslation();

  const isEditMode = settings.formMode === "UPDATE";
  const isDeleteMode = settings.formMode === "DELETE";

  const [images, setImages] = useState(null);
  const [newImages, setNewImages] = useState(null);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      rut: "",
      address: "",
      phone: "",
      email: "",
      notes: "",
      clientsInstallmentsAmountsCollection: [],
      clientsInstallmentsNumbersCollection: [],
      clientSettings: {},
      active: 0,
    },
  });

  let passwordMode = "HIDDEN"; // "UPDATE", "VISIBLE"

  const formModeConfig = {};

  if (isEditMode) {
    formModeConfig.SubmitButtonText = t("buttons.update");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.updating")}...`;
    formModeConfig.serverAction = async (data) => {
      data.Images = newImages;

      data.clientsInstallmentsAmountsCollection =
        data.clientsInstallmentsAmountsCollection.map((amount) => {
          return { InstallmentAmount: amount };
        });
      data.clientsInstallmentsNumbersCollection =
        data.clientsInstallmentsNumbersCollection.map((number) => {
          return { InstallmentNumber: number };
        });

      await settings.CRUDService.updateEntity(data, "base");
    };
  } else if (isDeleteMode) {
    formModeConfig.SubmitButtonText = t("buttons.delete");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.deleting")}...`;
    formModeConfig.serverAction = async () => {
      await settings.CRUDService.deleteEntity(settings.entityId, "base");
    };
  } else {
    formModeConfig.SubmitButtonText = t("buttons.create");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.creating")}...`;
    formModeConfig.serverAction = async (data) => {
      data.Images = images;

      data.clientsInstallmentsAmountsCollection =
        data.clientsInstallmentsAmountsCollection.map((amount) => {
          return { InstallmentAmount: amount };
        });
      data.clientsInstallmentsNumbersCollection =
        data.clientsInstallmentsNumbersCollection.map((number) => {
          return { InstallmentNumber: number };
        });

      await settings.CRUDService.createEntity(data, "base");
    };
  }

  useEffect(() => {
    if (isEditMode || isDeleteMode) {
      const fetchData = async () => {
        try {
          const data = await settings.CRUDService.getEntity(
            settings.entityId,
            "base"
          );

          for (const key in data) {
            setValue(key, data[key]);
          }

          setImages(JSON.parse(data.imagesUris)[0]);

          setValue(
            "clientsInstallmentsAmountsCollection",
            data.clientsInstallmentsAmountsCollection.map((element) => {
              return element.installmentAmount;
            })
          );

          setValue(
            "clientsInstallmentsNumbersCollection",
            data.clientsInstallmentsNumbersCollection.map((element) => {
              return element.installmentNumber;
            })
          );
        } catch (error) {
          console.error(t("forms.messages.operation.error"), error);
        }
      };

      fetchData();
    }
  }, [settings.entityId, setValue, settings.formMode]);

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      await formModeConfig.serverAction(data);

      settings.hideDialog();

      settings.toast.current.show({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
      });
    } catch (error) {
      if (error.response.status == 405) {
        settings.toast.current.show({
          severity: "error",
          summary: t("forms.messages.titles.error"),
          detail: t("forms.messages.operation.deleteErrorReference"),
          life: 3000,
        });
        settings.toast.current.show({
          severity: "info",
          summary: t("forms.messages.titles.suggestion"),
          detail: t("forms.messages.operation.activeInactiveOption"),
          life: 3000,
        });
      } else {
        settings.toast.current.show({
          severity: "error",
          summary: t("forms.messages.titles.error"),
          detail: t("forms.messages.operation.error"),
          life: 3000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewImages = (image) => {
    setNewImages(image);
    setImages(image);
    console.log(image);
  };

  const getFormErrorMessage = (error) => {
    return error && <small className="p-error">{error.message}</small>;
  };

  return (
    <React.Fragment>
      <div className={s.get()}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`${s.get("form")} ${settings.formMode !== "DELETE" ? "abm-create-form" : ""}`}
        >
          <Controller
            name="name"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.clients.name"),
              }),
              maxLength: {
                value: 100,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.clients.name"),
                  maxLength: 100,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="name">{t("entities.clients.name")}</label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.name)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="name"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="rut"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.clients.rut"),
              }),
              maxLength: {
                value: 12,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.clients.rut"),
                  maxLength: 12,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="rut">{t("entities.clients.rut")}</label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.rut)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="rut"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="address"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.clients.address"),
              }),
              maxLength: {
                value: 250,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.clients.address"),
                  maxLength: 250,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="address">
                    {t("entities.clients.address")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.address)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="address"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="phone"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.clients.phone"),
              }),
              maxLength: {
                value: 14,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.clients.phone"),
                  maxLength: 14,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="phone">{t("entities.clients.phone")}</label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.phone)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="phone"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="email"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.clients.email"),
              }),
              maxLength: {
                value: 100,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.clients.email"),
                  maxLength: 100,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="email">{t("entities.clients.email")}</label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.email)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="email"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="notes"
            control={control}
            rules={{
              maxLength: {
                value: 500,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.clients.notes"),
                  maxLength: 500,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="notes">{t("entities.clients.notes")}</label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.notes)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="notes"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />

          <Controller
            name="clientsInstallmentsAmountsCollection"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t(
                  "entities.clients.clientsInstallmentsAmountsCollection"
                ),
              }),
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="clientsInstallmentsAmountsCollection">
                    {t("entities.clients.clientsInstallmentsAmountsCollection")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(
                      errors.clientsInstallmentsAmountsCollection
                    )}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <Chips
                      id="clientsInstallmentsAmountsCollection"
                      value={field.value}
                      onChange={(e) => field.onChange(e.value)}
                      keyfilter="int"
                      className={classNames({
                        "p-invalid": fieldState.error,
                      })}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="clientsInstallmentsNumbersCollection"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t(
                  "entities.clients.clientsInstallmentsNumbersCollection"
                ),
              }),
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="clientsInstallmentsNumbersCollection">
                    {t("entities.clients.clientsInstallmentsNumbersCollection")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(
                      errors.clientsInstallmentsNumbersCollection
                    )}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <Chips
                      id="clientsInstallmentsNumbersCollection"
                      value={field.value}
                      onAdd={(e) => {
                        return e.value >= 1 && e.value <= 18;
                      }}
                      onChange={(e) => field.onChange(e.value)}
                      keyfilter="int"
                      className={classNames({
                        "p-invalid": fieldState.error,
                      })}
                      disabled={settings.formMode == "DELETE"}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="clientsSettings.annualRate"
            control={control}
            rules={{
              min: {
                value: 0.01,
                message: t("forms.validation.min", {
                  entity: t("entities.clients.clientsSettings.annualRate"),
                  min: 0.01,
                }),
              },
              max: {
                value: 1.2,
                message: t("forms.validation.max", {
                  entity: t("entities.clients.clientsSettings.annualRate"),
                  max: 1.2,
                }),
              },
            }}
            render={({ field }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="clientsSettings.annualRate">
                    {t("entities.clients.clientsSettings.annualRate")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.annualRate)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputNumber
                      id={field.name}
                      value={field.value ?? ""}
                      onValueChange={(e) => field.onChange(e.value)}
                      mode="decimal"
                      locale="de-DE"
                      maxFractionDigits={2}
                      minFractionDigits={2}
                      inputClassName={[
                        classNames({
                          "p-invalid": errors.annualRate,
                        }),
                      ]}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="clientsSettings.annualDefaultRate"
            control={control}
            rules={{
              min: {
                value: 0.01,
                message: t("forms.validation.min", {
                  entity: t(
                    "entities.clients.clientsSettings.annualDefaultRate"
                  ),
                  min: 0.01,
                }),
              },
              max: {
                value: 1.2,
                message: t("forms.validation.max", {
                  entity: t(
                    "entities.clients.clientsSettings.annualDefaultRate"
                  ),
                  max: 1.2,
                }),
              },
            }}
            render={({ field }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="clientsSettings.annualDefaultRate">
                    {t("entities.clients.clientsSettings.annualDefaultRate")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.annualDefaultRate)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputNumber
                      id={field.name}
                      value={field.value ?? ""}
                      onValueChange={(e) => field.onChange(e.value)}
                      mode="decimal"
                      locale="de-DE"
                      maxFractionDigits={2}
                      minFractionDigits={2}
                      inputClassName={[
                        classNames({
                          "p-invalid": errors.annualDefaultRate,
                        }),
                      ]}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="clientsSettings.administrationFeeRate"
            control={control}
            rules={{
              min: {
                value: 0.01,
                message: t("forms.validation.min", {
                  entity: t(
                    "entities.clients.clientsSettings.administrationFeeRate"
                  ),
                  min: 0.01,
                }),
              },
              max: {
                value: 1.2,
                message: t("forms.validation.max", {
                  entity: t(
                    "entities.clients.clientsSettings.administrationFeeRate"
                  ),
                  max: 1.2,
                }),
              },
            }}
            render={({ field }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="clientsSettings.administrationFeeRate">
                    {t(
                      "entities.clients.clientsSettings.administrationFeeRate"
                    )}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.administrationFeeRate)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputNumber
                      id={field.name}
                      value={field.value ?? ""}
                      onValueChange={(e) => field.onChange(e.value)}
                      mode="decimal"
                      locale="de-DE"
                      maxFractionDigits={2}
                      minFractionDigits={2}
                      inputClassName={[
                        classNames({
                          "p-invalid": errors.administrationFeeRate,
                        }),
                      ]}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <div className={s.get("form.field")}>
            <div className={s.get("form.field.label")}>
              <label htmlFor="logo">{t("entities.clients.logo")}</label>
            </div>
            <div className={s.get("form.field.value")}>
              <div className={s.get("form.field.value.error")}>
                {getFormErrorMessage(errors.images)}
              </div>
              <div className={s.get("form.field.value.control")}>
                <div className="flex">
                  <div className="flex w-9">
                    <ImageUpload
                      setImage={handleNewImages}
                      auto="true"
                      chooseLabel="Seleccionar imagen de logo de la empresa"
                      emptyText="Arrastra y suelta la imagen del logo aquí"
                    ></ImageUpload>
                  </div>
                  <div className="flex border-1 border-300 w-3 justify-content-center align-items-center">
                    <Image
                      src={images}
                      zoomSrc={images}
                      alt="images"
                      key="images"
                      width="120"
                      preview
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Controller
            name="active"
            control={control}
            rules={{}}
            render={({ field }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="active">{t("entities.clients.active")}</label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.active)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <Checkbox
                      inputId="active"
                      onChange={(e) => field.onChange(e.checked)}
                      checked={field.value}
                      disabled={settings.formMode == "DELETE"}
                      className="flex mt-2"
                    />
                  </div>
                </div>
              </div>
            )}
          />

          <div className="flex justify-content-between mt-6 mb-2">
            <Button
              label={t("buttons.cancel")}
              icon="pi pi-times"
              className="p-button-text btn-secondary-crud"
              onClick={() => settings.hideDialog("CANCEL")}
              type="button"
              disabled={isLoading}
            />
            <Button
              label={
                isLoading
                  ? formModeConfig.SubmitButtonTextWait
                  : formModeConfig.SubmitButtonText
              }
              icon="pi pi-check"
              className=" btn btn-primary btn-primary-crud"
              type="submit"
              disabled={isLoading}
            />
          </div>
        </form>
      </div>
    </React.Fragment>
  );
};

export default ClientsForm;
